import { hasRequiredFieldErrors, validateRequiredScanFields } from './submit-validation';
import { normalizeLinkedInInput } from './linkedin-qr';
import type { Scan, ScanSubmitInput, ScanSubmitResponse } from './scan';

/**
 * "Submit N ready drafts" — plans/2026-09-18-001-lead-scanner-heic-bulk-submit-plan.md
 * §2. A frontend-safe leaf (only `./scan` types, `./submit-validation`,
 * `./linkedin-qr` — no React, no Glaze, no `_shared`), same stance as
 * `photo-intake.ts`: this module holds the SEQUENCING and the SUBMIT-INPUT
 * mapping, which are the only parts with real behavior to get wrong, and
 * that's what's unit-tested here. The React/query wiring (the actual
 * `submitScan` call, cache seeding, toasts) lives in
 * `hooks/useBulkSubmit.ts`; the button/dialog UI lives in
 * `components/BulkSubmitDrafts.tsx`.
 *
 * No new server code: `POST /scans/:id/submit` (`api.ts:856`) is already
 * idempotent and CAS-safe per row (a 409 means another caller already
 * claimed it; a 502 means the fire failed and the row is already `error`
 * with `sfdcError`) — a client-side loop calling it N times needs nothing
 * new on the server.
 */

/**
 * "Ready" (Decision 1 of the plan): a `draft` row whose trimmed `name` and
 * `company` both pass the SAME required-field check the single-scan Submit
 * button uses (`submit-validation.ts`) — reused rather than a second
 * hand-rolled predicate. Ownership scoping (own rows only) is the caller's
 * job (`ScanListCard.tsx` compares `scannedByEmail` to the viewer), not
 * this function's — this predicate only knows about a row's own fields.
 */
export function isReadyDraft(scan: Pick<Scan, 'status' | 'name' | 'company'>): boolean {
  return scan.status === 'draft' && !hasRequiredFieldErrors(validateRequiredScanFields(scan));
}

/**
 * Maps a persisted `draft` row 1:1 onto `POST /scans/:id/submit`'s body,
 * mirroring `ScanReview.tsx`'s `buildSubmitPayload` but sourced from the
 * ROW's own persisted values instead of live form state (there is no form
 * here — the bulk button submits a draft exactly as it currently stands).
 * `linkedin` is re-normalized the same defensive way `buildSubmitPayload`
 * does, in case a row's `linkedin` was set by something other than the
 * review form's own `onBlur` (e.g. the QR-decode leg's raw PATCH).
 * `forceResubmit` is never set — the bulk button only ever targets `draft`
 * rows, which never need it (only "Update in Salesforce"/"Add note & resend"
 * on an already-`synced` row do).
 */
export function submitInputFromScan(scan: Scan): ScanSubmitInput {
  return {
    idempotencyKey: scan.idempotencyKey,
    sourceType: scan.sourceType,
    name: scan.name,
    company: scan.company,
    email: scan.email,
    linkedin: normalizeLinkedInInput(scan.linkedin),
    notes: scan.notes,
    routeToGtme: scan.routeToGtme,
    suppressFollowUp: scan.suppressFollowUp,
    campaignId: scan.campaignId,
    scannedByEmail: scan.scannedByEmail,
    matchedContactId: scan.matchedContactId,
  };
}

/** Same shape and value as `photo-intake.ts`'s `PHOTO_RESOLVE_CONCURRENCY`
 *  (Decision 3 of the plan): each submit does a server-side Clay execute
 *  loopback, and the scans-list poll shares the browser's ~6-connection cap. */
export const BULK_SUBMIT_CONCURRENCY = 3;

export type BulkSubmitOutcome =
  | { scanId: string; ok: true; response: ScanSubmitResponse }
  | { scanId: string; ok: false; error: string };

export interface BulkSubmitDeps {
  /** `useScanApi().submitScan` in the app; a stub in tests. */
  submitScan: (scanId: string, body: ScanSubmitInput) => Promise<ScanSubmitResponse>;
  /** Fired once per row as it settles (success or failure) — this is where
   *  `useBulkSubmit.ts` does its cache seeding and bumps its progress
   *  counter. Never awaited by the pool itself. */
  onScanSettled?: (outcome: BulkSubmitOutcome) => void;
}

export interface BulkSubmitResult {
  /** Scan ids that submitted successfully, in the SAME order as the input
   *  `scans` array (not completion order). */
  submitted: string[];
  /** Rows that failed, each with the failure's message. Order matches the
   *  input array's relative order among the failures. */
  failed: { scanId: string; error: string }[];
}

function errorMessage(err: unknown): string {
  return (err as Error)?.message ?? String(err);
}

/**
 * Order-preserving bounded pool, copied from `photo-intake.ts`'s
 * `runPhotoIntake` resolution loop (same reasoning: firing every submit at
 * once would queue behind the browser's per-origin connection cap and the
 * list's own poll). Each row is individually try/caught — a failed submit
 * never blocks or rolls back the others (Decision 5 of the plan): a 502
 * means the server already flipped that row to `error` with `sfdcError`, a
 * 409 means it was claimed elsewhere; either way this pool just records it
 * and moves on. Never rejects. Empty input makes zero calls.
 */
export async function runBulkSubmit(scans: Scan[], deps: BulkSubmitDeps): Promise<BulkSubmitResult> {
  if (scans.length === 0) return { submitted: [], failed: [] };

  const outcomes: BulkSubmitOutcome[] = new Array(scans.length);
  let nextIndex = 0;
  await Promise.all(
    Array.from({ length: Math.min(BULK_SUBMIT_CONCURRENCY, scans.length) }, async () => {
      for (;;) {
        const i = nextIndex++;
        if (i >= scans.length) return;
        const scan = scans[i];
        try {
          const response = await deps.submitScan(scan.id, submitInputFromScan(scan));
          const outcome: BulkSubmitOutcome = { scanId: scan.id, ok: true, response };
          outcomes[i] = outcome;
          deps.onScanSettled?.(outcome);
        } catch (err) {
          const outcome: BulkSubmitOutcome = { scanId: scan.id, ok: false, error: errorMessage(err) };
          outcomes[i] = outcome;
          deps.onScanSettled?.(outcome);
        }
      }
    }),
  );

  const submitted: string[] = [];
  const failed: { scanId: string; error: string }[] = [];
  for (const outcome of outcomes) {
    if (outcome.ok) submitted.push(outcome.scanId);
    else failed.push({ scanId: outcome.scanId, error: outcome.error });
  }
  return { submitted, failed };
}
